import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import ContextWindowMeter from "../common/ContextWindowMeter";
import { useDevopsWorkspace } from "../../contexts/DevopsWorkspaceContext";
import { useDevopsChat, type DevopsChatMessage } from "../../hooks/useDevopsAgentChat";

function AgentActivityList({ activities }: { activities: string[] }) {
  const visible = activities.slice(-3);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, maxWidth: "100%", bgcolor: "action.hover" }}
    >
      {visible.map((label, index) => {
        const isLast = index === visible.length - 1;
        return (
          <Box key={`${label}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
            {isLast ? (
              <CircularProgress size={14} />
            ) : (
              <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
            )}
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        );
      })}
    </Paper>
  );
}

function CopyableAssistantMessage({ message }: { message: DevopsChatMessage }) {
  const [copied, setCopied] = useState(false);
  const streaming = Boolean(message.streaming);
  const activities = message.activities ?? [];

  const copy = async () => {
    const text = message.content.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  if (streaming) {
    return <AgentActivityList activities={activities.length ? activities : ["Working…"]} />;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, maxWidth: "100%" }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <AgentChatMessageBubble role="assistant" content={message.content} />
      </Box>
      <Tooltip title={copied ? "Copied" : "Copy response"}>
        <IconButton
          size="small"
          aria-label="Copy response"
          onClick={copy}
          disabled={!message.content.trim()}
        >
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const DevopsAgentCompanion: React.FC = () => {
  const { stickyEntity, unbindEntity, closeAgent } = useDevopsWorkspace();
  const { messages, loading, error, contextUsage, sendMessage } = useDevopsChat();
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    sendMessage(input);
    setInput("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            DevOps agent
          </Typography>
          {stickyEntity ? (
            <Chip
              size="small"
              color="primary"
              label={`Bound to ${stickyEntity.name}`}
              onDelete={unbindEntity}
              sx={{ mt: 1 }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Bind an entity from a hub to keep this chat on that record.
            </Typography>
          )}
        </Box>
        <IconButton onClick={closeAgent} aria-label="Close DevOps agent" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 1.5, minHeight: 0 }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: message.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            {message.role === "user" ? (
              <AgentChatMessageBubble role="user" content={message.content} />
            ) : (
              <CopyableAssistantMessage message={message} />
            )}
          </Box>
        ))}
        <div ref={endRef} />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          px: 2,
          pt: 1,
          pb: 1.5,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {contextUsage && <ContextWindowMeter contextUsage={contextUsage} />}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", mt: contextUsage ? 1 : 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask or request a change…"
            value={input}
            disabled={loading}
            multiline
            maxRows={4}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <IconButton color="primary" onClick={handleSend} disabled={loading || !input.trim()}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default DevopsAgentCompanion;
